"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { EyebrowBadge, HeroGlow, Input } from "@/components/ui/";
import { Search, X } from "@/components/icons";
import { POSTS_PER_PAGE } from "./data";
import {
  HeroFeaturedCard,
  PostListItem,
  ResearchArchiveNewsletterForm,
  Pagination,
  CategoryFilterSidebar,
  CategoryFilterSkeleton,
  ResearchPostsLoader,
} from "./parts";
import type { ResearchPost } from "./types";

const ORDERED_CATEGORIES = ["All", "Report", "Analysis", "Study", "Benchmark", "Survey"];

export default function ResearchArchiveSection({
  initialPosts = [],
}: {
  initialPosts?: ResearchPost[];
}) {
  const prefersReducedMotion = useReducedMotion();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    };
  }, []);

  const heroPost = initialPosts[0];

  const categories = useMemo(() => {
    const cats = new Set(initialPosts.map((p) => p.category).filter(Boolean));
    return ORDERED_CATEGORIES.filter((c) => c === "All" || cats.has(c));
  }, [initialPosts]);

  const filteredPosts = useMemo(() => {
    return initialPosts.filter((post) => {
      if (post === heroPost) return false;
      const matchesCategory = activeCategory === "All" || post.category === activeCategory;
      const matchesSearch =
        searchQuery.trim() === "" ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [initialPosts, heroPost, activeCategory, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedPosts = filteredPosts.slice(
    (safePage - 1) * POSTS_PER_PAGE,
    safePage * POSTS_PER_PAGE
  );

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
    setIsFetching(true);
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => setIsFetching(false), 280);
    const el = document.getElementById("research-grid");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 160;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const el = document.getElementById("research-grid");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 160;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  if (!heroPost) return null;

  return (
    <main id="main-content" tabIndex={-1} className="relative pt-24 pb-16 sm:pt-32 sm:pb-32">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <HeroGlow align="left" />
      </div>

      <section className="relative mx-auto mb-10 max-w-[80rem] px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 flex items-end justify-between gap-6"
        >
          <div>
            <EyebrowBadge>Proxidize Research</EyebrowBadge>
            <h1 className="text-foreground mb-2 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Research
            </h1>
            <p className="text-muted-foreground light:text-foreground/80 max-w-xs text-sm leading-relaxed">
              In-depth reports, benchmarks, and industry data from the Proxidize research team.
            </p>
          </div>
        </motion.div>

        <HeroFeaturedCard post={heroPost} />

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="border-border -mx-4 mt-8 flex flex-col justify-between gap-5 border-t border-b px-4 py-7 sm:-mx-6 sm:flex-row sm:items-center sm:px-6 lg:-mx-8 lg:px-8"
        >
          <div>
            <p className="text-foreground text-sm font-semibold">Stay in the loop</p>
            <p className="text-muted-foreground mt-0.5 text-sm">
              Get new research reports delivered to your inbox.
            </p>
          </div>
          <ResearchArchiveNewsletterForm />
        </motion.div>
      </section>

      <div className="mx-auto mt-14 max-w-[80rem] px-4 sm:px-6 lg:px-8">
        <div className="mb-6 lg:hidden">
          <div className="relative">
            <Search
              size={14}
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search research…"
              className="bg-card h-9 w-full rounded-full pr-8 pl-8 text-xs"
              aria-label="Search research"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-10 lg:grid-cols-[1fr_300px] lg:gap-14 xl:grid-cols-[1fr_320px]">
          <div className="relative min-w-0 self-start">
            <AnimatePresence>
              {isFetching && (
                <motion.div
                  key="research-posts-loader"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="pointer-events-none absolute inset-x-0 top-32 z-10 flex justify-center"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <ResearchPostsLoader />
                </motion.div>
              )}
            </AnimatePresence>

            {filteredPosts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-24 text-center"
              >
                <p className="text-muted-foreground mb-2 text-lg">No research found.</p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("All");
                  }}
                  className="text-muted-foreground text-sm font-medium"
                >
                  Clear filters
                </button>
              </motion.div>
            ) : (
              <div
                className={
                  isFetching
                    ? "pointer-events-none opacity-40 transition-opacity duration-200"
                    : "transition-opacity duration-200"
                }
              >
                <div
                  id="research-grid"
                  className="mb-1 flex items-center justify-between gap-2 sm:gap-4"
                >
                  <h2 className="text-foreground text-xl font-bold">
                    {activeCategory === "All" ? "Latest research" : activeCategory}
                  </h2>
                  <span className="text-muted-foreground text-xs">
                    {filteredPosts.length} paper{filteredPosts.length !== 1 ? "s" : ""}
                    {totalPages > 1 && ` · Page ${safePage} of ${totalPages}`}
                  </span>
                </div>
                <div className="bg-border mb-2 h-px" aria-hidden="true" />
                <AnimatePresence mode="popLayout">
                  {pagedPosts.map((post, i) => (
                    <PostListItem key={post.href} post={post} index={i} />
                  ))}
                </AnimatePresence>
                <Pagination current={safePage} total={totalPages} onChange={handlePageChange} />
              </div>
            )}
          </div>

          <aside className="hidden lg:block" aria-label="Filter by category">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="sticky top-24 space-y-4"
            >
              <div className="relative" role="search">
                <Search
                  size={14}
                  className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2"
                  aria-hidden="true"
                />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search research…"
                  className="bg-card border-border h-10 w-full rounded-xl pr-9 pl-9 text-sm"
                  aria-label="Search research"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                    aria-label="Clear search"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              {categories.length <= 1 ? (
                <CategoryFilterSkeleton />
              ) : (
                <CategoryFilterSidebar
                  categories={categories}
                  activeCategory={activeCategory}
                  onCategoryChange={handleCategoryChange}
                />
              )}
            </motion.div>
          </aside>
        </div>
      </div>
    </main>
  );
}
